import React from 'react';
import Mapbox from '../../components/Mapbox';
import CurrentSpotDisplay from '../../components/CurrentSpotDisplay';
import Header from '../../layout/Header'
import MobileNav from '../../layout/MobileNav'
import { useSelector, useDispatch } from 'react-redux';
import { fetchSavedSpotsLiveData } from '../../store/slices/liveDataSlice';
import { fetchAllSpots } from '../../store/slices/savedSpotsSlice';
import LiveDataTable from '../../components/open-meteo/current/LiveDataTable';
import Slider from "react-slick";
import {Helmet} from "react-helmet";

// Custom Arrow Components
const NextArrow = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      className="hidden md:flex absolute -right-16 top-[42%] -translate-y-1/2 z-10 w-12 h-12 bg-custom-charcoal hover:bg-custom-charcoal/80 rounded-full shadow-lg items-center justify-center transition-all duration-200 group"
      aria-label="Next slide"
    >
      <svg 
        className="w-6 h-6 text-white group-hover:text-gray-200 transition-colors" 
        fill="none" 
        stroke="currentColor" 
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );
};

const PrevArrow = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      className="hidden md:flex absolute -left-16 top-[42%] -translate-y-1/2 z-10 w-12 h-12 bg-custom-charcoal hover:bg-custom-charcoal/80 rounded-full shadow-lg items-center justify-center transition-all duration-200 group"
      aria-label="Previous slide"
    >
      <svg 
        className="w-6 h-6 text-white group-hover:text-gray-200 transition-colors" 
        fill="none" 
        stroke="currentColor" 
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
      </svg>
    </button>
  );
};

const Home = () => {
  const dispatch = useDispatch();
  const savedSpots = useSelector((state) => state.savedSpots.spots);
  const savedSpotsLiveData = useSelector((state) => state.liveData.spots);
  const currentSpot = useSelector(state => state.savedLocations.currentSpot);
  const [slidesToShow, setSlidesToShow] = React.useState(1);
  const [mapHeight, setMapHeight] = React.useState(window.innerHeight - 120);
  const [open, setOpen] = React.useState(window.innerWidth >= 768);
  const spotsRef = React.useRef(null);
  const mapRef = React.useRef(null);
  const sliderRef = React.useRef(null);

  // React.useEffect(() => {
  //   setOpen(window.innerWidth >= 768 ? open : false);
  // }, [currentSpot]);

  // Sync slider with currentSpot selection
  React.useEffect(() => {
    if (currentSpot && sliderRef.current && Object.keys(savedSpots).length > 0) {
      // Find the index of the current spot in the savedSpots array
      const spotIndex = Object.values(savedSpots).findIndex(spot => spot.id === currentSpot.id);
      
      if (spotIndex !== -1) {
        // Go to the slide for this spot
        sliderRef.current.slickGoTo(spotIndex);
      } else {
        setOpen(window.innerWidth >= 768 ? open : false);
      }
    }
  }, [currentSpot, savedSpots]);

  React.useEffect(() => {
    dispatch(fetchSavedSpotsLiveData());
    dispatch(fetchAllSpots());
  }, [dispatch]);

  React.useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width >= 1460) {
        setSlidesToShow(5);
      } else if (width >= 1200) {
        setSlidesToShow(4);
      } else if (width >= 1024) {
        setSlidesToShow(3);
      } else if (width >= 768) {
        setSlidesToShow(2);
      } else {
        setSlidesToShow(1);
      }

      if (width < 768){
        setMapHeight(window.innerHeight - 120);
      } else {
        setMapHeight(window.innerHeight - 340);
      }
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const renderSpots = () => {
    
    const handleSlideClick = (e, spotId, index) => {
      // Only handle centering on mobile (centerMode is only on mobile)
      if (window.innerWidth < 768) {
        const slideElement = e.currentTarget.closest('.slick-slide');
        const isCentered = slideElement?.classList.contains('slick-center');
        
        // If not centered, prevent navigation and center this slide
        if (!isCentered) {
          e.preventDefault();
          sliderRef.current?.slickGoTo(index);
        }
      }
      // On desktop, allow all clicks (no centerMode)
    };

    return Object.values(savedSpots).map((spot, index) => (
      <div key={spot.id} className="px-2 h-full">
        <div 
          className="block h-full"
          onClick={(e) => handleSlideClick(e, spot.id, index)}
        >
          {spot && <LiveDataTable spot={spot} liveData={savedSpotsLiveData[spot.id]} />}
        </div>
      </div>
    ));
  }

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: slidesToShow,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 10000,
    easing: 'ease-in-out',
    nextArrow: slidesToShow > 1 ? <NextArrow /> : null,
    prevArrow: slidesToShow > 1 ? <PrevArrow /> : null,
    arrows: slidesToShow > 1,
    centerMode: slidesToShow === 1,
  };

  const spotsFound = Object.keys(savedSpots).length > 0

  return (
    <>
      <Helmet>
          <title>Map | Oreca Pro</title>
          <link rel="canonical" href={window.location.href} />
      </Helmet>
      <Header />
      <MobileNav />
    
      <div className='pt-[60px]'>
        {/* map */}
        <div id="map" className='relative' ref={mapRef}>
          <Mapbox width="100%" height={mapHeight} />
          {/* display current spot */}
          <div id="current-spot-display" className={`bg-black/30 backdrop-blur-lg p-5 rounded-lg  w-full max-w-[90%] md:max-w-[340px] z-[50] absolute bottom-[65px] md:bottom-[30px] translate-x-[-50%] left-1/2 md:translate-x-0 md:left-6 ${open ? 'open' : ''}`}>
            <CurrentSpotDisplay />
            
            {/* Scroll down button - inside spot display */}
            
          </div>
        </div>

        <div id="saved-spots" ref={spotsRef} className={`bg-custom-dark w-full  border-t border-custom-charcoal min-h-[280px] ${open ? 'open' : ''}`}>
            <button
                onClick={() => setOpen(!open)}
                id="toggle-saved-spots"
                aria-label="View all spots"
                className={`${open ? 'open' : ''} w-full py-3 text-sm text-white/70 hover:text-white transition-all duration-300 flex items-center justify-center gap-2 group relative md:hidden`}
              >
                {/* Bird wing chevron animation */}
                <svg 
                  className="w-8 h-4 transition-all duration-300 ease-out" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 32 16"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M2 12l14-10 14 10" className="transition-all duration-300"/>
                </svg>
            </button>
            <div className='mt-2 relative pb-[80px] px-5 md:px-20 mx-auto overflow-hidden md:mt-10'>
              {spotsFound ? (
                <Slider ref={sliderRef} key={slidesToShow} {...settings}>
                  {renderSpots()}
                </Slider>
              ) : (
                <p className='text-white/70'>No saved spots found.</p>
              )}
            </div>
        </div>
      </div>
    </>
  );
};

export default Home;
